import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import moment from "moment";
import { getChannels, getChannelGenres } from "../../api/ltv/ltv";
import { filterChannelsByGenre } from "../../utils/channelGenreFilter";
import { SessionContext } from "../../context/SessionContext";
import { getBroadcastCopy } from "../../statics/broadcastCopy";
import localizationStrings from "../../statics/localization";
import CinemaIcon from "../../components/cinema/CinemaIcon";
import TvGuideRow from "../../components/tvGuideRow/TvGuideRow";
import {
  HOUR_WIDTH,
  searchChannels
} from "../../components/tvGuideRow/guideTimeline";
import ModalPortal from "../../components/modalPortal";
import ProgramDetail from "../../components/programDetail";
import ModalRouting from "../../components/modalRouting/ModalRouting";
import "moment/locale/ar";

export default function TvGuide() {
  const copy = getBroadcastCopy();
  const locale =
    { arb: "ar" }[localizationStrings.getLanguage()] || "en";
  const session = useContext(SessionContext);
  const {
    selectedChannel,
    setSelectedChannel,
    setSelectedCutvProgram,
    setPath,
    setChannelItems
  } = session;
  const history = useHistory();
  const location = useLocation();
  const [channels, setChannels] = useState([]);
  const [genres, setGenres] = useState([]);
  const [genreId, setGenreId] = useState(2);
  const [term, setTerm] = useState("");
  const [status, setStatus] = useState("loading");
  const [retry, setRetry] = useState(0);
  const [visibleCount, setVisibleCount] = useState(15);
  const [now, setNow] = useState(Date.now());
  const [selectedDate, setSelectedDate] = useState(() =>
    moment().format("YYYY-MM-DD")
  );
  const [selection, setSelection] = useState(null);
  const timeline = useRef(null);
  const dateStrip = useRef(null);
  const today = moment(now).format("YYYY-MM-DD");
  const dayStart = moment(selectedDate).startOf("day").valueOf();
  const dayEnd = moment(selectedDate).add(1, "day").startOf("day").valueOf();
  const timelineWidth = ((dayEnd - dayStart) / 3600000) * HOUR_WIDTH;
  const todaySelected = selectedDate === today;
  const nowOffset = ((now - dayStart) / 3600000) * HOUR_WIDTH;
  const dates = useMemo(
    () =>
      Array.from({ length: 14 }, (_, index) =>
        moment(today).add(index - 7, "days")
      ),
    [today]
  );
  const filters = [
    { id: 2, name: copy.all },
    { id: 5, name: copy.favorites },
    ...genres.filter((item) => item.id !== 2 && item.id !== 5)
  ];
  const filtered = useMemo(
    () =>
      searchChannels(filterChannelsByGenre(channels, { id: genreId }), term),
    [channels, genreId, term]
  );

  useEffect(() => {
    setPath("/dashboard/tvguide");
    window.scrollTo(0, 0);
    const timer = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, [setPath]);
  useEffect(() => {
    let active = true;
    setStatus("loading");
    getChannels()
      .then((response) => {
        if (!active) return;
        if (!Array.isArray(response?.data))
          throw new Error("Invalid channel list");
        setChannels(response.data);
        setChannelItems(response.data);
        setStatus("ready");
      })
      .catch(() => {
        if (active) setStatus("error");
      });
    getChannelGenres()
      .then((items) => {
        if (active) setGenres(Array.isArray(items) ? items : []);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [retry, setChannelItems]);
  useEffect(() => {
    setVisibleCount(15);
  }, [term, genreId]);
  useEffect(() => {
    if (timeline.current) {
      const offset =
        selectedDate === moment().format("YYYY-MM-DD")
          ? ((Date.now() - dayStart) / 3600000) * HOUR_WIDTH - 70
          : 0;
      timeline.current.scrollLeft = Math.max(0, offset) * (getComputedStyle(timeline.current).direction === "rtl" ? -1 : 1);
    }
  }, [selectedDate, dayStart]);
  useEffect(() => {
    const selected =
      dateStrip.current &&
      dateStrip.current.querySelector("[aria-pressed='true']");
    if (selected)
      dateStrip.current.scrollLeft =
        selected.offsetLeft -
        dateStrip.current.clientWidth / 2 +
        selected.clientWidth / 2;
  }, [today]);

  const watch = (channel) => {
    setSelectedChannel(channel);
    setSelectedCutvProgram(null);
    setPath(`/dashboard/livetv/${channel.id}`);
    // Live TV retains its existing entitlement and PIN checks.
    history.push(`/dashboard/livetv/${channel.id}`);
  };
  const goNow = () => {
    setSelectedDate(today);
    if (todaySelected && timeline.current)
      timeline.current.scrollTo({
        left: Math.max(0, nowOffset - 70) * (getComputedStyle(timeline.current).direction === "rtl" ? -1 : 1),
        behavior: "smooth"
      });
  };
  const moveTimeline = (direction) => {
    if (timeline.current)
      timeline.current.scrollBy({
        left: direction * HOUR_WIDTH * 2 * (getComputedStyle(timeline.current).direction === "rtl" ? -1 : 1),
        behavior: "smooth"
      });
  };
  return (
    <div className="container atlas-guide atlas-broadcast">
      <header className="broadcast-heading">
        <div>
          <span className="broadcast-eyebrow">{copy.guideEyebrow}</span>
          <h1>{copy.guide}</h1>
          <p>{copy.guideNote}</p>
        </div>
        <Link
          className="broadcast-link"
          to={`/dashboard/livetv/${selectedChannel?.id || "all"}`}
        >
          <CinemaIcon name="tv" />
          {copy.live}
          <CinemaIcon name="arrow" />
        </Link>
      </header>
      <div
        className="epg-date-strip"
        ref={dateStrip}
        role="group"
        aria-label={copy.day}
      >
        {dates.map((date) => {
          const key = date.format("YYYY-MM-DD");
          const offset = date.diff(moment(today), "days");
          return (
            <button
              key={key}
              type="button"
              aria-pressed={selectedDate === key}
              onClick={() => setSelectedDate(key)}
            >
              <span>
                {offset === 0
                  ? copy.today
                  : offset === -1
                  ? copy.yesterday
                  : offset === 1
                  ? copy.tomorrow
                  : date.clone().locale(locale).format("ddd")}
              </span>
              <strong>
                {date.format("DD")}{" "}
                <small>{date.clone().locale(locale).format("MMM")}</small>
              </strong>
              {offset === 0 && <i />}
            </button>
          );
        })}
      </div>
      <div className="broadcast-toolbar">
        <label className="broadcast-search">
          <CinemaIcon name="search" />
          <input
            type="search"
            value={term}
            aria-label={copy.search}
            placeholder={copy.search}
            onChange={(event) => setTerm(event.target.value)}
          />
          {term && (
            <button
              type="button"
              aria-label={copy.clear}
              onClick={() => setTerm("")}
            >
              <CinemaIcon name="close" />
            </button>
          )}
        </label>
        <div
          className="broadcast-categories"
          role="group"
          aria-label={copy.category}
        >
          {filters.map((genre) => (
            <button
              type="button"
              key={genre.id}
              aria-pressed={genreId === genre.id}
              onClick={() => setGenreId(genre.id)}
            >
              {genre.name}
            </button>
          ))}
        </div>
      </div>
      <div className="epg-navigation">
        <span>
          <strong>{filtered.length}</strong> {copy.channels}
          <span className="epg-day-caption">
            {" "}
            · {moment(selectedDate).locale(locale).format("D MMMM YYYY")}
          </span>
        </span>
        <div>
          <button className="epg-now-button" type="button" onClick={goNow}>
            <i />
            {copy.now}
          </button>
          <button
            type="button"
            aria-label={copy.earlier}
            onClick={() => moveTimeline(-1)}
          >
            <CinemaIcon name="previous" />
          </button>
          <button
            type="button"
            aria-label={copy.later}
            onClick={() => moveTimeline(1)}
          >
            <CinemaIcon name="next" />
          </button>
        </div>
      </div>
      {status === "error" ? (
        <div className="broadcast-empty" role="alert">
          {copy.channelError}
          <button onClick={() => setRetry((value) => value + 1)}>
            {copy.retry}
          </button>
        </div>
      ) : status === "ready" && !filtered.length ? (
        <div className="broadcast-empty">{copy.noChannels}</div>
      ) : (
        <div
          className="epg-viewport"
          ref={timeline}
          tabIndex="0"
          aria-label={copy.guide}
          style={{ "--epg-width": `${timelineWidth}px` }}
        >
          <div className="epg-board">
            <div className="epg-time-header">
              <div className="epg-corner">{copy.channels}</div>
              <div className="epg-hours">
                {Array.from(
                  { length: Math.ceil((dayEnd - dayStart) / 3600000) },
                  (_, hour) => (
                    <span key={hour} style={{ insetInlineStart: hour * HOUR_WIDTH }}>
                      {moment(dayStart).add(hour, "hours").format("HH:mm")}
                    </span>
                  )
                )}
                {todaySelected && (
                  <span className="epg-clock" style={{ insetInlineStart: nowOffset }}>
                    {moment(now).format("HH:mm")}
                  </span>
                )}
              </div>
            </div>
            <div className="epg-rows">
              {status === "loading" && (
                <div className="epg-row-message" role="status">
                  {copy.loading}
                </div>
              )}
              {filtered.slice(0, visibleCount).map((channel) => (
                <TvGuideRow
                  key={channel.id}
                  channel={channel}
                  viewportRef={timeline}
                  date={moment(selectedDate).format("DD.MM.YYYY")}
                  dayStart={dayStart}
                  dayEnd={dayEnd}
                  now={now}
                  selectedChannel={selectedChannel}
                  onWatch={watch}
                  onClickProgram={(program, current, sourceChannel) =>
                    setSelection({ program, current, channel: sourceChannel })
                  }
                />
              ))}
              {todaySelected && (
                <div
                  className="epg-now-line"
                  aria-hidden="true"
                  style={{
                    insetInlineStart: `calc(var(--epg-channel-width) + ${nowOffset}px)`
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
      <div className="epg-footer">
        <p>{copy.hint}</p>
        {visibleCount < filtered.length && (
          <button
            type="button"
            onClick={() => setVisibleCount((count) => count + 15)}
          >
            {copy.more}
            <CinemaIcon name="down" />
          </button>
        )}
      </div>
      {selection && (
        <ModalPortal viewClass="vod-modal">
          <ProgramDetail
            programData={selection.program}
            prevueCode={
              selection.program.epgProgramId || selection.program.prevuecode
            }
            isCurrent={selection.current}
            closeModal={(current) => {
              setSelection(null);
              if (current) watch(selection.channel);
            }}
            channels={channels}
            fromTvGuide
            setSelectedCutvProgram={setSelectedCutvProgram}
            selectedChannel={selection.channel}
            userFrameConfig={session.userFrameConfig}
            userInfo={session.userInfo}
            location={location}
            history={history}
            setPlayUrl={session.setPlayUrl}
            playUrl={session.playUrl}
            hasLoggedIn={session.hasLoggedIn}
          />
        </ModalPortal>
      )}
      <ModalRouting />
    </div>
  );
}
